import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';

import { Button } from '../base/Button';
import { Select } from '../base/Select';
import { Toggle } from '../base/Toggle';
import { Modal } from '../../layouts/Modal';
import { TextField } from '../base/TextField';
import { SectionLoading } from '../shared/SectionLoading';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import {
  ApplianceModelClassification,
  ApplianceModelEndianness,
  IApplianceModel,
} from '../../types/Appliance/ApplianceModel';
import useApplianceTypes from '../../data/appliance/useApplianceTypes';
import useApplianceModels from '../../data/appliance/useApplianceModel';
import useApplianceModelsMutations from '../../data/appliance/useApplianceModelMutations';
import {
  BaudRateOptions,
  DataBitsOptions,
  ParityOptions,
  StopBitsOptions,
} from '../../types/PlanetDevice/PlanetDevice';
import { ManufacturerSelect } from '../shared/ManufacturerSelect';
import { ModelSelect } from '../shared/ModelSelect';
import { ClickablePill } from '../base/ClickablePill';
import { Pill } from '../base/Pill';

export interface CreateEditApplianceModelProps {
  open: boolean;
  onClose: Dispatch<SetStateAction<boolean>>;
  hardwareModel?: IApplianceModel;
}

export function CreateEditApplianceModel({
  open,
  onClose,
  hardwareModel,
}: CreateEditApplianceModelProps) {
  const { register, reset, handleSubmit, watch, setValue } = useForm();
  const { applianceTypes } = useApplianceTypes();
  const { applianceModels } = useApplianceModels();
  const { createApplianceModel, updateApplianceModel } =
    useApplianceModelsMutations();
  const [checked, setChecked] = useState(true);
  const [partial, setPartial] = useState(false);
  const [actionProfiles, setActionProfiles] = useState(true);
  const [actionTransformations, setActionTransformations] = useState(true);
  const [dataProfiles, setDataProfiles] = useState(true);
  const [dataOptions, setDataOptions] = useState(true);
  const [dataTransformations, setDataTransformations] = useState(true);
  const [magicNumberValue, setMagicNumberValue] = useState('');

  const applianceModelClassifications = useMemo(
    () => [
      ...Object.values(ApplianceModelClassification).filter((ct) =>
        isNaN(Number(ct))
      ),
    ],
    []
  );

  const hardwareModelEndiannesses = useMemo(
    () => [
      ...Object.values(ApplianceModelEndianness)
        .filter((e) => isNaN(Number(e)))
        .map((endianess, i) => ({ label: endianess, value: i })),
    ],
    []
  );

  const handleClose = () => {
    onClose(false);
    setTimeout(() => reset({}), 200);
  };

  const handleCreate = (data: any) => {
    createApplianceModel.mutate({
      ...data,
      serialSettings: {
        ...data.serialSettings,
        parity: parseInt(data.serialSettings.parity),
      },
      classification: parseInt(data.classification),
      disabled: checked,
      partial: partial,
      hardwareModelId:
        data.hardwareModelId !== '' ? data.hardwareModelId : null,
      duplicateActionProfiles: actionProfiles,
      duplicateActionTransformations: actionTransformations,
      duplicateDataProfiles: dataProfiles,
      duplicateDataOptions: dataOptions,
      duplicateDataTransformations: dataTransformations,
      magicNumbers: data.magicNumbers
        ? data.magicNumbers.map((num: string) => parseInt(num))
        : [],
    });
    handleClose();
  };

  const handleUpdate = (data: any) => {
    updateApplianceModel.mutate({
      ...hardwareModel,
      ...data,
      serialSettings: {
        ...data.serialSettings,
        parity: parseInt(data.serialSettings.parity),
      },
      classification: parseInt(data.classification),
      endianness: parseInt(data.endianness),
      disabled: checked,
      partial: partial,
      hardwareModelId: null,
      magicNumbers: data.magicNumbers
        ? data.magicNumbers.map((num: string) => parseInt(num))
        : [],
    });
    handleClose();
  };

  const handleCopyID = async () =>
    await navigator.clipboard.writeText(hardwareModel?.id ?? '');

  useEffect(() => {
    if (hardwareModel) {
      setChecked(hardwareModel.disabled);
      setPartial(hardwareModel.partial);
      reset({
        name: hardwareModel.name,
        hardwareTypeId: hardwareModel.hardwareTypeId,
        hardwareManufacturerId: hardwareModel.hardwareManufacturerId,
        classification: hardwareModel.classification,
        serialSettings: {
          baudRate: hardwareModel.serialSettings.baudRate,
          dataBits: hardwareModel.serialSettings.dataBits,
          parity: hardwareModel.serialSettings.parity,
          stopBits: hardwareModel.serialSettings.stopBits,
        },
        address: hardwareModel.address,
        endianness: hardwareModel.endianness,
        magicNumbers: hardwareModel.magicNumbers
          ? hardwareModel.magicNumbers.map((num) => num.toString())
          : [],
      });
    } else {
      setChecked(true);
      setPartial(false);
      reset({ address: 1 });
    }
  }, [reset, hardwareModel]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${hardwareModel ? 'Edit' : 'Create'} Hardware Model`}
      description={`Use this form to ${
        hardwareModel ? 'edit' : 'create'
      } a hardware model`}
    >
      {applianceModels.isLoading && applianceTypes.isLoading ? (
        <SectionLoading />
      ) : (
        <form
          onSubmit={handleSubmit(hardwareModel ? handleUpdate : handleCreate)}
          className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-4 sm:gap-6 sm:gap-y-4"
        >
          {hardwareModel && (
            <div className="grid grid-cols-8 gap-x-2 items-end">
              <div className="col-span-7">
                <TextField
                  fullWidth
                  label="ID"
                  disabled
                  value={hardwareModel?.id}
                />
              </div>
              <Button onClick={handleCopyID} className="h-[42px]">
                <ClipboardDocumentIcon />
              </Button>
            </div>
          )}
          <Select {...register('hardwareTypeId', { value: '' })} label="Type">
            <option disabled hidden value="">
              Please Select
            </option>
            {applianceTypes.data?.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>
          {watch('hardwareTypeId') && (
            <ManufacturerSelect
              {...register('hardwareManufacturerId')}
              type={watch('hardwareTypeId')}
            />
          )}
          {watch('hardwareManufacturerId') && (
            <>
              <TextField {...register('name')} label="Name" />
              <Select {...register('classification')} label="Classification">
                {applianceModelClassifications.map((c, i) => (
                  <option key={c} value={i}>
                    {c}
                  </option>
                ))}
              </Select>

              <Select
                {...register('serialSettings.baudRate', { value: 9600 })}
                label="Baud Rate"
              >
                <option disabled hidden value={0}>
                  Please Select
                </option>
                {BaudRateOptions.map((x, i) => (
                  <option key={i} value={x}>
                    {x}
                  </option>
                ))}
              </Select>

              <Select
                {...register('serialSettings.dataBits', { value: 8 })}
                label="Data Bits"
              >
                <option disabled hidden value={0}>
                  Please Select
                </option>
                {DataBitsOptions.map((x, i) => (
                  <option key={i} value={x}>
                    {x}
                  </option>
                ))}
              </Select>

              <Select
                {...register('serialSettings.parity', { value: '0' })}
                label="Parity"
              >
                {ParityOptions.map(
                  (x: { key: string; value: number }, i: number) => (
                    <option key={i} value={x.value}>
                      {x.key}
                    </option>
                  )
                )}
              </Select>
              <Select
                {...register('serialSettings.stopBits', { value: 1 })}
                label="Stop Bits"
              >
                <option disabled hidden value={0}>
                  Please Select
                </option>
                {StopBitsOptions.map((x, i) => (
                  <option key={i} value={x}>
                    {x}
                  </option>
                ))}
              </Select>

              <Select
                {...register('endianness', { value: 0 })}
                label="Endianness"
              >
                {hardwareModelEndiannesses.map((x, i) => (
                  <option key={i} value={x.value}>
                    {`${x.label
                      .toString()
                      .substring(0, 3)
                      .toLocaleUpperCase()} ${x.label
                      .toString()
                      .substring(3, 6)
                      .toUpperCase()}`}
                  </option>
                ))}
              </Select>

              <TextField {...register('address')} label="Address" />

              {hardwareModel === undefined && (
                <ModelSelect
                  {...register('hardwareModelId')}
                  manufacturer={watch('hardwareManufacturerId')}
                  type={watch('hardwareTypeId')}
                  label="Duplicate From"
                />
              )}

              <div className="sm:col-span-2">
                <div>
                  <p className="text-lg">Magic Numbers</p>
                  <p className="text-sm text-gray-500">
                    Values listed here will be stripped from readings and
                    replaced with unavailable
                  </p>
                </div>
                <div className="my-4 flex flex-row items-end space-x-2">
                  <TextField
                    label="Value"
                    type="number"
                    value={magicNumberValue}
                    onChange={(e) => {
                      const val = e.currentTarget.value;
                      // Only allow integer values
                      if (/^\d*$/.test(val)) {
                        setMagicNumberValue(val);
                      }
                    }}
                  />
                  <Button
                    className="h-[42px]"
                    onClick={() => {
                      const current = watch('magicNumbers') ?? [];

                      if (
                        magicNumberValue !== '' &&
                        !current.includes(magicNumberValue)
                      ) {
                        setValue('magicNumbers', [
                          ...current,
                          magicNumberValue,
                        ]);
                        setMagicNumberValue('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                <div className="rounded border border-primary dark:bg-gray-700 min-h-[64px] p-2 flex flex-row flex-wrap items-center">
                  {watch('magicNumbers')?.map((x: string, i: number) => (
                    <ClickablePill
                      key={i}
                      onIconClick={() => {
                        const current = watch('magicNumbers') ?? [];
                        setValue(
                          'magicNumbers',
                          current.filter((_: any, idx: number) => idx !== i)
                        );
                      }}
                      classes="mb-1 bg-gray-300 dark:bg-gray-800 flex-shrink h-auto"
                    >
                      {x}
                    </ClickablePill>
                  ))}
                </div>
              </div>
            </>
          )}
          {watch('hardwareModelId') && (
            <div className="col-span-2 inline-flex mt-4">
              <Toggle
                className="mr-2"
                label="Action Profiles"
                checked={actionProfiles}
                onChange={setActionProfiles}
              />
              <Toggle
                className="mr-2"
                label="Action Transformations"
                checked={actionTransformations}
                onChange={setActionTransformations}
              />
              <Toggle
                className="mr-2"
                label="Data Profiles"
                checked={dataProfiles}
                onChange={setDataProfiles}
              />
              <Toggle
                className="mr-2"
                label="Data Options"
                checked={dataOptions}
                onChange={setDataOptions}
              />
              <Toggle
                className="mr-2"
                label="Data Transformations"
                checked={dataTransformations}
                onChange={setDataTransformations}
              />
            </div>
          )}
          <div className="mt-6 flex sm:col-span-2 justify-between">
            <div className="space-x-4">
              <Button type="submit">
                {hardwareModel ? 'Update' : 'Create'}
              </Button>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
            </div>
            <div className="space-x-4 flex">
              <Toggle label="Partial" checked={partial} onChange={setPartial} />
              <Toggle
                label="Disabled"
                checked={checked}
                onChange={setChecked}
              />
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}
